import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Dimensions } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../contexts/ThemeContext';
import Footer from '../components/Footer';
import { formatCurrency, formatNumber } from '../utils/formatters';

const { width } = Dimensions.get('window');

function parseDateInput(str) {
  // Accept YYYY-MM-DD and fallback to Date.parse
  const d = new Date(str);
  if (!isNaN(d.getTime())) return d;
  return new Date();
}

function daysBetween(d0, d1) {
  const ms = 24 * 3600 * 1000;
  return Math.round((d1 - d0) / ms);
}

function xnpv(rate, cashflows) {
  if (cashflows.length === 0) return 0;
  const start = cashflows[0].date;
  return cashflows.reduce((sum, cf) => {
    const t = daysBetween(start, cf.date) / 365;
    return sum + (cf.amount / Math.pow(1 + rate, t));
  }, 0);
}

function xirr(cashflows, guess = 0.1) {
  // Newton-Raphson with simple safeguards
  const maxIter = 100;
  const tol = 1e-7;
  let rate = guess;
  for (let i = 0; i < maxIter; i++) {
    const f = xnpv(rate, cashflows);
    // derivative of NPV w.r.t rate
    const start = cashflows[0].date;
    const df = cashflows.reduce((sum, cf) => {
      const t = daysBetween(start, cf.date) / 365;
      return sum + (-t * cf.amount / Math.pow(1 + rate, t + 1));
    }, 0);
    if (Math.abs(df) < 1e-12) break;
    const newRate = rate - f / df;
    if (!isFinite(newRate)) break;
    if (Math.abs(newRate - rate) < tol) return newRate;
    rate = newRate;
  }
  return rate; // may be approximate
}

export default function XIRRCalculatorScreen() {
  const { colors } = useTheme();
  const navigation = useNavigation();

  const today = new Date();
  const sixMonthsAgo = new Date(today.getTime() - 180 * 24 * 3600 * 1000);
  const oneYearAgo = new Date(today.getTime() - 365 * 24 * 3600 * 1000);

  const [rows, setRows] = useState([
    { amount: '-100000', date: oneYearAgo.toISOString().slice(0, 10) },
    { amount: '-5000', date: sixMonthsAgo.toISOString().slice(0, 10) },
    { amount: '140000', date: today.toISOString().slice(0, 10) },
  ]);

  const num = (v, d = 0) => {
    const n = parseFloat(String(v).replace(/,/g, ''));
    if (isNaN(n)) return d;
    return n;
  };

  const cf = useMemo(() => rows.map(r => ({ amount: num(r.amount, 0), date: parseDateInput(r.date) })), [rows]);

  const totals = useMemo(() => {
    const inflow = cf.filter(c => c.amount > 0).reduce((s, c) => s + c.amount, 0);
    const outflow = cf.filter(c => c.amount < 0).reduce((s, c) => s + c.amount, 0);
    let irr = 0;
    try { irr = xirr(cf, 0.1); } catch { irr = 0; }
    const npvAtIRR = xnpv(irr, cf);
    return { inflow, outflow, irrPct: irr * 100, npvAtIRR };
  }, [cf]);

  const updateRow = (index, field, value) => {
    setRows(prev => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const addRow = () => setRows(prev => [...prev, { amount: '0', date: new Date().toISOString().slice(0, 10) }]);
  const removeRow = (index) => setRows(prev => prev.filter((_, i) => i !== index));
  const resetAll = () => {
    setRows([
      { amount: '-100000', date: oneYearAgo.toISOString().slice(0, 10) },
      { amount: '-5000', date: sixMonthsAgo.toISOString().slice(0, 10) },
      { amount: '140000', date: today.toISOString().slice(0, 10) },
    ]);
  };

  const currency = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.heroSection}>
        <View style={styles.heroContent}>
          <Text style={styles.heroTitle}>XIRR Calculator</Text>
          <Text style={styles.heroSubtitle}>Annualized return for irregular cashflows</Text>
        </View>
      </View>

      <View style={styles.calculatorContainer}>
        <View style={styles.calculatorRow}>
          <View style={styles.leftPanel}>
            <View style={styles.card}>
              <Text style={styles.sectionTitle}>Cashflows</Text>
              {rows.map((row, idx) => (
                <View key={idx} style={{ marginBottom: 8 }}>
                  <View style={styles.row}>
                    <View style={[styles.inputGroup, styles.flexItem]}>
                      <Text style={styles.label}>Amount (₹)</Text>
                      <TextInput value={row.amount} onChangeText={(v) => updateRow(idx, 'amount', v)} style={styles.input} keyboardType="numeric" placeholder="e.g. -10000 or 15000" />
                    </View>
                    <View style={[styles.inputGroup, styles.flexItem]}>
                      <Text style={styles.label}>Date (YYYY-MM-DD)</Text>
                      <TextInput value={row.date} onChangeText={(v) => updateRow(idx, 'date', v)} style={styles.input} placeholder="YYYY-MM-DD" />
                    </View>
                  </View>
                  {rows.length > 1 && (
                    <TouchableOpacity onPress={() => removeRow(idx)} style={styles.removeBtn}><Text style={styles.removeBtnText}>Remove</Text></TouchableOpacity>
                  )}
                </View>
              ))}
              <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
                <TouchableOpacity style={styles.buttonSecondary} onPress={addRow}><Text style={styles.buttonTextAlt}>Add Row</Text></TouchableOpacity>
              </View>
              <Text style={[styles.paragraph, { marginTop: 8 }]}>Use negative amounts for investments (outflows) and positive amounts for withdrawals/redemptions (inflows).</Text>
            </View>
          </View>

          <View style={styles.rightPanel}>
            <View style={styles.card}>
              <View style={styles.panelHeader}>
                <Text style={styles.panelTitle}>Cashflow Summary</Text>
                <Text style={styles.panelSubtitle}>Totals by sign</Text>
              </View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Total Inflows</Text><Text style={styles.summaryValue}>{currency.format(totals.inflow)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Total Outflows</Text><Text style={styles.summaryValue}>{currency.format(totals.outflow)}</Text></View>
            </View>

            <View style={styles.card}>
              <View style={styles.panelHeader}>
                <Text style={styles.panelTitle}>XIRR Result</Text>
                <Text style={styles.panelSubtitle}>Annualized internal rate of return</Text>
              </View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>XIRR</Text><Text style={styles.summaryValue}>{formatNumber(totals.irrPct, 2)}%</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>NPV at XIRR</Text><Text style={styles.summaryValue}>{currency.format(totals.npvAtIRR)}</Text></View>
              <View style={{ marginTop: 12, flexDirection: 'row', gap: 8 }}>
                <TouchableOpacity style={styles.buttonPrimary} onPress={resetAll}><Text style={styles.buttonText}>Reset</Text></TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </View>

      {renderSEO(navigation)}
      {renderFAQ()}
      <Footer />
    </ScrollView>
  );
}

function renderSEO(navigation) {
  return (
    <View style={styles.seoSection}>
      <Text style={styles.seoTitle}>About XIRR</Text>
      <Text style={styles.seoText}>
        XIRR calculates the annualized internal rate of return for irregular cashflows by solving for the discount rate that makes the net present value (NPV) of cashflows zero. It’s ideal for SIPs, redemptions, deposits, and withdrawals at varying dates.
      </Text>

      <Text style={styles.seoSubtitle}>Computation details</Text>
      <Text style={styles.seoText}>
        We use a Newton–Raphson approach to approximate the rate. The first cashflow’s date is used as the reference. Provide at least one negative (investment) and one positive (redemption) amount for meaningful results.
      </Text>

      <Text style={styles.seoSubtitle}>Input guidance</Text>
      <Text style={styles.seoText}>
        Enter outflows as negative numbers and inflows as positive. Keep the earliest transaction first to align with the reference date assumption.
      </Text>

      <View style={styles.linksRow}>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('SIPCalculator')}>
          <Text style={styles.linkText}>SIP Calculator</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('CAGRCalculator')}>
          <Text style={styles.linkText}>CAGR Calculator</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('MutualFundReturnsCalculator')}>
          <Text style={styles.linkText}>Mutual Fund Returns</Text>
        </TouchableOpacity>
      </View>

      <Text style={styles.disclaimer}>
        Results may be approximate if convergence is difficult. This tool is for education only. Verify with financial statements or official tools when required.
      </Text>
    </View>
  );
}

function renderFAQ() {
  return (
    <View style={styles.faqContainer}>
      <Text style={styles.faqTitle}>Frequently Asked Questions</Text>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>Why do I need both inflows and outflows?</Text>
        <Text style={styles.faqAnswer}>Without both signs, IRR has no solution or is not meaningful. Include investments (negative) and redemptions (positive).</Text>
      </View>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>Does the order of cashflows matter?</Text>
        <Text style={styles.faqAnswer}>The first cashflow’s date anchors time differences. Keep earliest first for clarity. The rate is based on time deltas regardless of subsequent ordering.</Text>
      </View>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>Why is my XIRR negative?</Text>
        <Text style={styles.faqAnswer}>A negative XIRR indicates losses or that inflows were insufficient relative to outflows after factoring time.</Text>
      </View>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>Is XIRR the same as CAGR?</Text>
        <Text style={styles.faqAnswer}>No. CAGR assumes a single lump-sum investment and final value. XIRR handles multiple dated cashflows and is more general.</Text>
      </View>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>The result didn’t converge—what can I do?</Text>
        <Text style={styles.faqAnswer}>Ensure dates are valid and include both inflows and outflows. Try adjusting the sequence or values. Extreme or conflicting cashflows may hinder convergence.</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8fafc' },
  content: { paddingBottom: 24 },
  heroSection: { backgroundColor: '#4F46E5', paddingVertical: 18, paddingHorizontal: 16 },
  heroContent: { maxWidth: 1200, alignSelf: 'center' },
  heroTitle: { fontSize: 22, fontWeight: '800', color: '#ffffff', marginBottom: 4 },
  heroSubtitle: { fontSize: 14, color: '#e0e7ff' },
  calculatorContainer: { width: '100%', alignSelf: 'stretch', paddingHorizontal: 16, paddingVertical: 12 },
  calculatorRow: { flexDirection: 'row', gap: 24 },
  leftPanel: { flex: 0.6 },
  rightPanel: { flex: 0.4 },
  card: { backgroundColor: '#ffffff', borderRadius: 12, padding: 16, marginTop: 16, marginHorizontal: 16, borderWidth: 1, borderColor: '#e5e7eb' },
  sectionTitle: { fontSize: 18, fontWeight: '700', color: '#0f172a', marginBottom: 12, marginHorizontal: 16 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  inputGroup: { marginVertical: 6 },
  input: { backgroundColor: '#f1f5f9', borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10 },
  label: { fontSize: 13, color: '#334155', marginBottom: 6 },
  flexItem: { flex: 1 },
  panelHeader: { alignItems: 'center', marginBottom: 10, paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: '#e5e7eb' },
  panelTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a' },
  panelSubtitle: { fontSize: 12, color: '#64748b', marginTop: 2 },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6 },
  summaryLabel: { color: '#334155' },
  summaryValue: { color: '#0f172a', fontWeight: '600' },
  buttonPrimary: { backgroundColor: '#4F46E5', paddingVertical: 10, paddingHorizontal: 14, borderRadius: 8 },
  buttonSecondary: { backgroundColor: '#EEF2FF', borderWidth: 1, borderColor: '#c7d2fe', paddingVertical: 10, paddingHorizontal: 14, borderRadius: 8 },
  buttonText: { color: '#ffffff', fontWeight: '600' },
  buttonTextAlt: { color: '#1e293b', fontWeight: '600' },
  removeBtn: { alignSelf: 'flex-start', marginTop: 6, backgroundColor: '#fee2e2', borderColor: '#fecaca', borderWidth: 1, borderRadius: 6, paddingVertical: 6, paddingHorizontal: 10 },
  removeBtnText: { color: '#991b1b', fontWeight: '600' },
  seoSection: { backgroundColor: '#ffffff', borderRadius: 12, padding: 16, marginTop: 16, borderWidth: 1, borderColor: '#e5e7eb', marginHorizontal: 16 },
  seoTitle: { fontSize: 18, fontWeight: '700', color: '#0f172a', marginBottom: 8 },
  seoSubtitle: { fontSize: 16, fontWeight: '600', color: '#0f172a', marginTop: 12, marginBottom: 6 },
  seoText: { color: '#334155', lineHeight: 20 },
  linksRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 10 },
  linkButton: { backgroundColor: '#EEF2FF', borderColor: '#c7d2fe', borderWidth: 1, borderRadius: 8, paddingVertical: 8, paddingHorizontal: 12 },
  linkText: { color: '#1e293b', fontWeight: '600' },
  disclaimer: { color: '#64748b', fontSize: 12, marginTop: 10 },
  faqContainer: { backgroundColor: '#ffffff', borderRadius: 12, padding: 16, marginTop: 16, borderWidth: 1, borderColor: '#e5e7eb', marginHorizontal: 16 },
  faqTitle: { fontSize: 18, fontWeight: '700', color: '#0f172a', marginBottom: 8 },
  faqItem: { marginBottom: 10 },
  faqQuestion: { fontSize: 15, fontWeight: '600', color: '#0f172a' },
  faqAnswer: { color: '#334155', marginTop: 4, lineHeight: 20 },
});